import { Navigate } from 'react-router-dom';

import DashboardLayout from '../shell/layouts/DashboardLayout.jsx';
import DocsStandaloneLayout from '../shell/layouts/DocsStandaloneLayout.jsx';

import { ProtectedRoute } from '../authentication/ProtectedRoute.jsx';
import { RoleProtectedRoute } from '../authentication/RoleProtectedRoute.jsx';
import { RequireGameAccessOutlet } from '../authentication/RequireGameAccess.jsx';
import { RequireActiveScopeOutlet } from '../authentication/RequireActiveScope.jsx';
import { CanonicalGameLink, CanonicalPortfolioLink } from './CanonicalLink.jsx';
import LandingRedirect from './LandingRedirect.jsx';
import { lazyWithRetry } from '../utils/lazyWithRetry.js';
import { PAGES, COMPONENTS } from '../config/pagePaths.generated.js';

const DataConnectionsList = lazyWithRetry(() => import('../pages/game/game-settings/data-connections/DataConnectionsList.jsx'));
const ConnectionRunsList = lazyWithRetry(() => import('../pages/game/game-settings/data-connections/ConnectionRunsList.jsx'));
const RunDetailsPage = lazyWithRetry(() => import('../pages/game/game-settings/data-connections/RunDetailsPage.jsx'));

const Homepage = lazyWithRetry(() => import('../pages/hub/homepage/Homepage.jsx'));
const ComponentsPage = lazyWithRetry(() => import('../pages/hub/components/ComponentsPage.jsx'));

const GamesPage = lazyWithRetry(() => import('../pages/game/hub/GamesPage.jsx'));
const GameOverview = lazyWithRetry(() => import('../pages/game/overview/GameOverview.jsx'));
const NitroChatPage = lazyWithRetry(() => import('../pages/game/nitro-chat/NitroChatPage.jsx'));
const ScopePagePlaceholder = lazyWithRetry(() => import('../pages/common/ScopePagePlaceholder.jsx'));
const PortfoliosPage = lazyWithRetry(() => import('../pages/portfolio/PortfoliosPage.jsx'));
const PortfolioSettingsPage = lazyWithRetry(() => import('../pages/portfolio/settings/PortfolioSettingsPage.jsx'));
const PortfolioDashboardPage = lazyWithRetry(() => import('../pages/portfolio/dashboard/PortfolioDashboardPage.jsx'));
const GameOnboarding = lazyWithRetry(() => import('../pages/game/onboarding/GameOnboarding.jsx'));
const GameSettingsStepPage = lazyWithRetry(() => import('../pages/game/game-settings/SettingsPage.jsx'));
const SegmentsPage = lazyWithRetry(() => import('../pages/game/segments/SegmentsPage.jsx'));
const SegmentBuilderPage = lazyWithRetry(() => import('../pages/game/segments/SegmentBuilderPage.jsx'));
const SegmentDetailPage = lazyWithRetry(() => import('../pages/game/segments/SegmentDetailPage.jsx'));
const PlayerAttributesPage = lazyWithRetry(() => import('../pages/game/player-attributes/PlayerAttributes.jsx'));
const ConfigsPage = lazyWithRetry(() => import('../pages/game/configs/ConfigsPage.jsx'));
const ConfigFileDetailPage = lazyWithRetry(() => import('../pages/game/configs/ConfigFileDetailPage.jsx'));
const ConfigBranchEditorPage = lazyWithRetry(() => import('../pages/game/configs/ConfigBranchEditorPage.jsx'));
const LiveEventsPage = lazyWithRetry(() => import('../pages/game/live-events/LiveEventsPage.jsx'));
const LiveEventDetailPage = lazyWithRetry(() => import('../pages/game/live-events/LiveEventDetailPage.jsx'));
const UpcomingEventsPage = lazyWithRetry(() => import('../pages/game/live-services/upcoming/UpcomingEventsPage.jsx'));
const UpcomingDatePage = lazyWithRetry(() => import('../pages/game/live-services/upcoming/UpcomingDatePage.jsx'));
const LiveServicesOverviewPage = lazyWithRetry(() => import('../pages/game/live-services/overview/LiveServicesOverviewPage.jsx'));
const FlywheelsPage = lazyWithRetry(() => import('../pages/game/live-services/flywheels/FlywheelsPage.jsx'));
const FlywheelPage = lazyWithRetry(() => import('../pages/game/live-services/flywheels/FlywheelPage.jsx'));
const ExperimentsPage = lazyWithRetry(() => import('../pages/game/live-services/experiments/ExperimentsPage.jsx'));
const NewExperimentPage = lazyWithRetry(() => import('../pages/game/live-services/experiments/NewExperimentPage.jsx'));
const ExperimentDashboardPage = lazyWithRetry(() => import('../pages/game/live-services/experiments/ExperimentDashboardPage.jsx'));

// Data Intelligence — the Event Intelligence surfaces, per game.
const EventsPage = lazyWithRetry(() => import('../pages/game/data-intelligence/event-intelligence/events/EventsPage.jsx'));
const EventGroupsPage = lazyWithRetry(() => import('../pages/game/data-intelligence/event-intelligence/event-groups/EventGroupsPage.jsx'));
const MetricCatalogPage = lazyWithRetry(() => import('../pages/game/data-intelligence/event-intelligence/metric-catalog/MetricCatalogPage.jsx'));
const ParamsPage = lazyWithRetry(() => import('../pages/game/data-intelligence/event-intelligence/params/ParamsPage.jsx'));

// Analytics.
const GameDashboard = lazyWithRetry(() => import('../pages/game/analytics/game-dashboard/GameDashboardNew.jsx'));
const GameDecisionCenter = lazyWithRetry(() => import('../pages/game/analytics/decision-center/GameDecisionCenter.jsx'));
const AIWorkspace = lazyWithRetry(() => import('../pages/game/analytics/decision-center/ai-workspace/AIWorkspace.jsx'));

// AI Studio.
const WorkflowStudio = lazyWithRetry(() => import('../pages/game/ai-studio/workflow-studio/Workflow.jsx'));
const QueryLibrary = lazyWithRetry(() => import('../pages/game/ai-studio/query-library/QueryLibrary.jsx'));
const QueryLibraryQuestionForm = lazyWithRetry(() => import('../pages/game/ai-studio/query-library/QueryLibraryQuestionForm.jsx'));
const SkillRegistry = lazyWithRetry(() => import('../pages/game/ai-studio/skill-registry/SkillRegistry.jsx'));
const Alerts = lazyWithRetry(() => import('../pages/game/ai-studio/alerts/Alerts.jsx'));

const NotificationsPage = lazyWithRetry(() => import('../pages/notifications/NotificationsPage.jsx'));
const ProfilePage = lazyWithRetry(() => import('../pages/user/profile/Profile.jsx'));
const AICreditsPage = lazyWithRetry(() => import('../pages/user/ai-credits/AICredits.jsx'));
const OrgAILimitsPage = lazyWithRetry(() => import('../pages/user/ai-credits/OrgAILimitsPage.jsx'));
const LimitOverridesPage = lazyWithRetry(() => import('../pages/user/ai-credits/LimitOverridesPage.jsx'));
const AICreditsLanding = lazyWithRetry(() => import('../pages/user/ai-credits/AICreditsLanding.jsx'));
const ReleasesPage = lazyWithRetry(() => import('../pages/user/releases/ReleasesPage.jsx'));
const SdksPage = lazyWithRetry(() => import('../pages/user/releases/SdksPage.jsx'));
const SupportPage = lazyWithRetry(() => import('../pages/user/help/support/SupportPage.jsx'));
const DocsPage = lazyWithRetry(() => import('../pages/user/help/docs/DocsPage.jsx'));
const StatusPage = lazyWithRetry(() => import('../pages/user/help/status/StatusPage.jsx'));

const userDashboardRoutes = [
    {
        // Where sign-in and every "home" land (LANDING_PATH): no shell, it only decides and redirects.
        path: 'landing',
        element: (
            <ProtectedRoute>
                <LandingRedirect />
            </ProtectedRoute>
        ),
    },
    {
        // Out of the nav since 26 October 2026, so it opens by URL only. Ungated: no RoleProtectedRoute and no
        // RequireGameAccess wrapper.
        path: 'homepage',
        element: (
            <ProtectedRoute>
                <DashboardLayout dashboardType="user" />
            </ProtectedRoute>
        ),
        children: [
            {
                path: '',
                element: <Homepage />,
            },
        ],
    },
    {
        // Blank placeholder. Ungated, same reasoning as /homepage above.
        path: 'components',
        element: (
            <ProtectedRoute>
                <DashboardLayout dashboardType="user" />
            </ProtectedRoute>
        ),
        children: [
            {
                path: '',
                element: <ComponentsPage />,
            },
        ],
    },
    // Scoped-view landings. Ungated like /homepage — their nav entries carry no pagePath, so the
    // scope stays reachable while the surfaces are placeholders. Each path is also what makes its
    // scope resolvable from the URL (see routes/navScopes.jsx).
    {
        path: 'game',
        element: (
            <ProtectedRoute>
                <DashboardLayout dashboardType="user" />
            </ProtectedRoute>
        ),
        children: [
            { path: '', element: <GamesPage /> },
            // Onboarding starts from the "Create game" modal, which creates the game and
            // routes to /game/new/:gameId/details — a bare /game/new has no game to set up.
            { path: 'new', element: <Navigate to="/game" replace /> },
            { path: 'new/:gameId', element: <GameOnboarding /> },
            { path: 'new/:gameId/:step', element: <GameOnboarding /> },
            // Everything scoped to ONE game sits behind the lifecycle guard. These are operational
            // surfaces — they are what replaced the per-game workspace tabs, which already used this
            // guard — so a suspended or sunset game gets the lock card rather than a page that still
            // looks half-usable: amber with a "reactivate from Game Management" CTA for suspended,
            // red and CTA-less for sunset, which is terminal.
            //
            // Deliberately does NOT cover the routes above it. The bare /game is the games LIST (a hub
            // page, not scoped to any game) and /game/new/* is the creation wizard, whose game is
            // DRAFT — and DRAFT is not frozen (see utils/gameLifecycle.js: it is still being set up).
            // The guard also fails open while the lifecycle is loading or unknown, so there is no
            // flash of the lock card on a healthy game.
            {
                // Accessibility gate OUTSIDE the lifecycle one: "you hold no game in this org" is a
                // different answer from "this game is suspended", and it has to win — asking someone
                // with no access at all to go reactivate a game is nonsense. Without it these pages
                // rendered for a brand-new org member with nothing granted, each one waiting on game
                // data it was never allowed to read, so the content area sat on a spinner instead of
                // the "No game access — request it" card that exists for exactly this.
                // CanonicalGameLink puts the game's current name + id link in the address bar (old links still open).
                element: <><CanonicalGameLink /><RequireGameAccessOutlet /></>,
                children: [{
                    element: <RequireActiveScopeOutlet />,
                    children: [
                    { path: ':gameSlug', element: <GameOverview /> },
                    // One route for both: /chat (a fresh chat) and /chat/<convId> (that conversation, so a refresh
                    // or a shared link reopens it). Optional segment keeps the page mounted across the switch.
                    { path: ':gameSlug/chat/:convId?', element: <NitroChatPage /> },
                    { path: ':gameSlug/settings/:settingsKey', element: <GameSettingsStepPage /> },
                    { path: ':gameSlug/chat', element: <RoleProtectedRoute pagePath={PAGES.CHAT}><ScopePagePlaceholder /></RoleProtectedRoute> },
                    { path: ':gameSlug/segments', element: <RoleProtectedRoute pagePath={PAGES.SEGMENTATION}><SegmentsPage /></RoleProtectedRoute> },
                    // Static before dynamic so /segments/new is the builder, not a segment called "new".
                    { path: ':gameSlug/segments/new', element: <RoleProtectedRoute pagePath={PAGES.SEGMENTATION}><SegmentBuilderPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/segments/:segmentId', element: <RoleProtectedRoute pagePath={PAGES.SEGMENTATION}><SegmentDetailPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/segments/:segmentId/edit', element: <RoleProtectedRoute pagePath={PAGES.SEGMENTATION}><SegmentBuilderPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/player-attributes', element: <RoleProtectedRoute pagePath={PAGES.PLAYER_ATTRIBUTES}><PlayerAttributesPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/configs', element: <RoleProtectedRoute pagePath={PAGES.GAME_CONFIG}><ConfigsPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/configs/:fileId', element: <RoleProtectedRoute pagePath={PAGES.GAME_CONFIG}><ConfigFileDetailPage /></RoleProtectedRoute> },
                    // A branch is addressed by its lineage id, not the version document id — versions
                    // are a panel inside the branch, not a URL of their own.
                    { path: ':gameSlug/configs/:fileId/branches/:branchId', element: <RoleProtectedRoute pagePath={PAGES.GAME_CONFIG}><ConfigBranchEditorPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/live-events', element: <RoleProtectedRoute pagePath={PAGES.LIVE_EVENTS}><LiveEventsPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/live-events/:eventId', element: <RoleProtectedRoute pagePath={PAGES.LIVE_EVENTS}><LiveEventDetailPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/live-services', element: <LiveServicesOverviewPage /> },
                    { path: ':gameSlug/live-services/experiments', element: <RoleProtectedRoute pagePath={PAGES.EXPERIMENTS}><ExperimentsPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/live-services/experiments/new', element: <RoleProtectedRoute pagePath={PAGES.EXPERIMENTS}><NewExperimentPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/live-services/experiments/:experimentId', element: <RoleProtectedRoute pagePath={PAGES.EXPERIMENTS}><ExperimentDashboardPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/live-services/experiments/:experimentId/edit', element: <RoleProtectedRoute pagePath={PAGES.EXPERIMENTS}><NewExperimentPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/live-services/flywheels', element: <RoleProtectedRoute pagePath={PAGES.FLYWHEELS}><FlywheelsPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/live-services/flywheels/:flywheelId', element: <RoleProtectedRoute pagePath={PAGES.FLYWHEELS}><FlywheelPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/live-services/upcoming', element: <RoleProtectedRoute pagePath={PAGES.UPCOMING_EVENTS}><UpcomingEventsPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/live-services/upcoming/:timelineItemId/:occStart', element: <RoleProtectedRoute pagePath={PAGES.UPCOMING_EVENTS}><UpcomingDatePage /></RoleProtectedRoute> },
                    // Analytics. The active dashboard's name lives in the URL so a tab survives a
                    // refresh and can be linked to.
                    { path: ':gameSlug/dashboard', element: <RoleProtectedRoute pagePath={PAGES.GAME_DASHBOARD}><GameDashboard /></RoleProtectedRoute> },
                    { path: ':gameSlug/dashboard/:name', element: <RoleProtectedRoute pagePath={PAGES.GAME_DASHBOARD}><GameDashboard /></RoleProtectedRoute> },
                    // AI Recommendations, and the workspace you open one insight into. Static before
                    // dynamic so /ai-workspace is the workspace, not an insight called "ai-workspace".
                    { path: ':gameSlug/ai-recommendations', element: <RoleProtectedRoute pagePath={PAGES.GAME_DECISION_CENTER}><GameDecisionCenter /></RoleProtectedRoute> },
                    { path: ':gameSlug/ai-recommendations/ai-workspace', element: <RoleProtectedRoute pagePath={PAGES.GAME_DECISION_CENTER}><AIWorkspace /></RoleProtectedRoute> },
                    { path: ':gameSlug/ai-recommendations/ai-workspace/:insightId', element: <RoleProtectedRoute pagePath={PAGES.GAME_DECISION_CENTER}><AIWorkspace /></RoleProtectedRoute> },
                    // Data Intelligence. RBAC is applied per CONTROL inside each page (the same
                    // COMPONENTS.* gates they carried in the old shell), not at the route — matching
                    // every other page in this scope.
                    { path: ':gameSlug/data-intelligence', element: <Navigate to="events" replace /> },
                    { path: ':gameSlug/data-intelligence/events', element: <RoleProtectedRoute pagePath={PAGES.EVENTS}><EventsPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/data-intelligence/event-groups', element: <RoleProtectedRoute pagePath={PAGES.EVENT_GROUPS}><EventGroupsPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/data-intelligence/metric-catalog', element: <RoleProtectedRoute pagePath={PAGES.GAME_CLIENT_METRIC_CATALOG}><MetricCatalogPage /></RoleProtectedRoute> },
                    { path: ':gameSlug/data-intelligence/params', element: <RoleProtectedRoute pagePath={PAGES.GAME_CLIENT_PARAMS}><ParamsPage /></RoleProtectedRoute> },
                    // AI Studio. The question form is a full page rather than a modal, so New and
                    // Edit are routes of their own — static before dynamic, as everywhere else.
                    { path: ':gameSlug/ai-studio', element: <Navigate to="workflow" replace /> },
                    { path: ':gameSlug/ai-studio/workflow', element: <RoleProtectedRoute pagePath={PAGES.AI_WORKFLOW}><WorkflowStudio /></RoleProtectedRoute> },
                    { path: ':gameSlug/ai-studio/query-library', element: <RoleProtectedRoute pagePath={PAGES.QUERY_LIBRARY}><QueryLibrary /></RoleProtectedRoute> },
                    { path: ':gameSlug/ai-studio/query-library/new', element: <RoleProtectedRoute pagePath={PAGES.QUERY_LIBRARY} componentPath={COMPONENTS.QUERY_LIBRARY_CREATE_QUESTION} minLevel="FULL_ACCESS"><QueryLibraryQuestionForm /></RoleProtectedRoute> },
                    { path: ':gameSlug/ai-studio/query-library/edit/:questionId', element: <RoleProtectedRoute pagePath={PAGES.QUERY_LIBRARY} componentPath={COMPONENTS.QUERY_LIBRARY_MUTATE_QUESTION} minLevel="FULL_ACCESS"><QueryLibraryQuestionForm /></RoleProtectedRoute> },
                    { path: ':gameSlug/ai-studio/skill-registry', element: <RoleProtectedRoute pagePath={PAGES.SKILL_REGISTRY}><SkillRegistry /></RoleProtectedRoute> },
                    { path: ':gameSlug/ai-studio/alerts', element: <RoleProtectedRoute pagePath={PAGES.AI_ALERTS}><Alerts /></RoleProtectedRoute> },
                    { path: ':gameSlug/*', element: <ScopePagePlaceholder /> },
                    ],
                }],
            },
        ],
    },
    {
        path: 'portfolio',
        element: (
            <ProtectedRoute>
                {/* Puts the portfolio's current name + id link in the address bar (old links still open). */}
                <CanonicalPortfolioLink />
                <DashboardLayout dashboardType="user" />
            </ProtectedRoute>
        ),
        // Gated on the Portfolio page, as /v1/web/portfolios is: a role without it neither sees the section in the
        // nav nor reaches these by URL.
        children: [
            { path: '', element: <RoleProtectedRoute pagePath={PAGES.PORTFOLIO}><PortfoliosPage /></RoleProtectedRoute> },
            { path: ':portfolioSlug', element: <RoleProtectedRoute pagePath={PAGES.PORTFOLIO}><PortfolioDashboardPage /></RoleProtectedRoute> },
            { path: ':portfolioSlug/settings', element: <RoleProtectedRoute pagePath={PAGES.PORTFOLIO}><PortfolioSettingsPage /></RoleProtectedRoute> },
            { path: ':portfolioSlug/*', element: <RoleProtectedRoute pagePath={PAGES.PORTFOLIO}><ScopePagePlaceholder /></RoleProtectedRoute> },
        ],
    },
    {
        path: 'user-dashboard',
        element: (
            <ProtectedRoute>
                <DashboardLayout dashboardType="user" />
            </ProtectedRoute>
        ),
        children: [
            // All user-dashboard pages render game-context data — gate the
            // whole block behind RequireGameAccess so users without any game
            // access see the placeholder instead of empty charts.
            {
                element: <RequireGameAccessOutlet />,
                children: [
                    {
                        path: '',
                        element: <Navigate to="data-connections" replace />,
                    },
                    {
                        path: 'data-connections',
                        element: <RoleProtectedRoute pagePath={PAGES.DATA_CONNECTIONS}><DataConnectionsList /></RoleProtectedRoute>,
                    },
                    {
                        path: 'data-connections/:connectionId/runs',
                        element: <RoleProtectedRoute pagePath={PAGES.DATA_CONNECTIONS}><ConnectionRunsList /></RoleProtectedRoute>,
                    },
                    {
                        path: 'data-connections/:connectionId/runs/:runId',
                        element: <RoleProtectedRoute pagePath={PAGES.DATA_CONNECTIONS}><RunDetailsPage /></RoleProtectedRoute>,
                    },
                ],
            },
        ],
    },
    // ─── Placeholder pages for bottom-pinned nav items ─────────────
    // Settings (app/account), Notifications, Help — all use the same
    // reusable ComingSoonPage component with different title/icon props.
    {
        // App Settings is now Profile's "Platform Settings" tab — its four one-setting tabs became
        // four sections on that page. Kept as a redirect so existing links and bookmarks land on the
        // tab rather than 404.
        path: 'app-settings',
        element: <Navigate to="/profile/platform" replace />,
    },
    {
        // Account-level AI Credits (LLM credit pool + per-user usage). Top-level
        // route like Profile / App Settings — DashboardLayout (user) so the page
        // renders its own PageHeader + .content-body directly. RBAC-gated on the
        // /ai-credits page so Game Users see own usage + pool, Game Owners see all.
        path: 'ai-credits',
        element: (
            <ProtectedRoute>
                <DashboardLayout dashboardType="user" />
            </ProtectedRoute>
        ),
        children: [
            {
                // AI Credits usage is per-game-client — show the "scope suspended" card when the selected
                // scope is suspended/sunset, like the other game-context pages. (Fails open when no
                // game is selected, so the account-level view still renders.)
                element: <RequireActiveScopeOutlet />,
                children: [
                    // One URL per page, so each is a nav row that can be linked and highlighted:
                    // Organization limits (org-wide), the game's Overview / Ledger / Limits (one
                    // page sharing a tab strip), and super users' All clients. The bare path picks
                    // the landing for the role.
                    {
                        path: '',
                        element: <RoleProtectedRoute pagePath="/ai-credits"><AICreditsLanding /></RoleProtectedRoute>,
                    },
                    {
                        path: 'org-limits',
                        element: <RoleProtectedRoute pagePath="/ai-credits"><OrgAILimitsPage /></RoleProtectedRoute>,
                    },
                    { path: 'game', element: <Navigate to="overview" replace /> },
                    {
                        path: 'game/:section',
                        element: <RoleProtectedRoute pagePath="/ai-credits"><AICreditsPage view="game" /></RoleProtectedRoute>,
                    },
                    {
                        path: 'clients',
                        element: <RoleProtectedRoute superUserOnly pagePath="/ai-credits"><AICreditsPage view="clients" /></RoleProtectedRoute>,
                    },
                    {
                        // Any org's maximum AI limits (LNG's override) - super users only, like the API.
                        path: 'limit-overrides',
                        element: <RoleProtectedRoute superUserOnly pagePath="/ai-credits"><LimitOverridesPage /></RoleProtectedRoute>,
                    },
                ],
            },
        ],
    },
    {
        // Same layout choice as the Notifications route — DashboardLayout
        // (not GameDecisionCenterLayout) so the Profile page can render
        // its own PageHeader + .content-body directly without the extra
        // wrapper that would double the horizontal padding.
        path: 'profile',
        element: (
            <ProtectedRoute>
                <DashboardLayout dashboardType="user" />
            </ProtectedRoute>
        ),
        children: [
            // Sections are sidebar items, so each needs its own URL for the nav to link to and
            // highlight. The bare /profile renders the same component, which redirects to a section
            // (honouring a legacy ?tab= if one is present — notification CTAs use that form).
            {
                path: '',
                element: <ProfilePage />,
            },
            {
                path: ':section',
                element: <ProfilePage />,
            },
        ],
    },
    {
        // Uses DashboardLayout (not GameDecisionCenterLayout) so the page's
        // own .content-header and .content-body render as direct children of
        // .content — matching Remote Config and preventing the doubled
        // horizontal padding that GameDecisionCenterLayout's own .content-body
        // wrapper would introduce.
        path: 'notifications',
        element: (
            <ProtectedRoute>
                <DashboardLayout dashboardType="user" />
            </ProtectedRoute>
        ),
        children: [
            {
                path: '',
                element: <NotificationsPage />,
            },
        ],
    },
    {
        // Releases — API release notes + the client SDK download centre. Bottom-pinned like Help
        // and, like it, ungated: every signed-in user can read them whatever their role. Global, not
        // per-game (the same SDK packages and service versions apply to every game), which is why
        // they sit at the top level rather than under /game/:gameSlug.
        path: 'releases',
        element: (
            <ProtectedRoute>
                <DashboardLayout dashboardType="user" />
            </ProtectedRoute>
        ),
        children: [
            { path: '', element: <Navigate to="apis" replace /> },
            { path: 'apis', element: <ReleasesPage /> },
            { path: 'sdks', element: <SdksPage /> },
        ],
    },
    {
        // In-app Help surfaces — only Support lives inside the dashboard shell.
        // Docs is intentionally routed through its own standalone layout below
        // so it can be opened in a separate browser tab.
        // Uses DashboardLayout (not GameDecisionCenterLayout) so help pages can
        // render their own `.content-header` + `.content-body` as direct
        // children of `.content` without the extra content-body wrapper that
        // GameDecisionCenterLayout adds.
        path: 'help',
        element: (
            <ProtectedRoute>
                <DashboardLayout dashboardType="user" />
            </ProtectedRoute>
        ),
        children: [
            {
                path: '',
                element: <Navigate to="support" replace />,
            },
            {
                path: 'support',
                element: <SupportPage />,
            },
            {
                path: 'status',
                element: <StatusPage />,
            },
        ],
    },
    {
        // Standalone docs window — opened via window.open() from the main app
        // so it feels like a dedicated reference tab (no sidebar / header).
        //
        // NOT wrapped in <ProtectedRoute>: auth state lives in sessionStorage
        // which is per-tab, so a freshly opened tab has no user until Firebase
        // rehydrates async — ProtectedRoute would redirect to /not-authorized
        // before that lands. Docs are reference content (no user data), so
        // public access is fine.
        path: 'help/docs',
        element: <DocsStandaloneLayout />,
        children: [
            {
                path: '',
                element: <DocsPage />,
            },
            {
                path: ':topic',
                element: <DocsPage />,
            },
        ],
    },
];

export default userDashboardRoutes;
